import type { SupportLineTermsAndConditions } from "../../../models/support-line";

const PAGE_MARGIN = 20;
const FOOTER_OFFSET = 10;
const PT_TO_MM = 0.3528;
const LINE_HEIGHT_FACTOR = 1.4;

export const TERMS_TITLE = "SupportLine 24/7 Terms and Conditions";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// validFrom is a calendar date; parsing it as a Date would shift it by the viewer's time zone.
export function formatTermsDate(isoDate: string): string {
  const [year, month, day] = isoDate.slice(0, 10).split("-").map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

export function termsPdfFileName(version: string): string {
  return `SupportLine-Terms-and-Conditions-v${version}.pdf`;
}

export async function downloadTermsPdf(
  terms: SupportLineTermsAndConditions
): Promise<void> {
  // Loaded on demand: the Support modal ships with the shared header of every app.
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;
  const bottomLimit = pageHeight - PAGE_MARGIN;
  let y = PAGE_MARGIN;

  const write = (
    text: string,
    fontSize: number,
    style: "normal" | "bold",
    spacingAfter: number
  ) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(fontSize);
    const lineHeight = fontSize * PT_TO_MM * LINE_HEIGHT_FACTOR;
    const lines: string[] = doc.splitTextToSize(text, contentWidth);
    for (const line of lines) {
      if (y + lineHeight > bottomLimit) {
        doc.addPage();
        y = PAGE_MARGIN;
      }
      doc.text(line, PAGE_MARGIN, y, { baseline: "top" });
      y += lineHeight;
    }
    y += spacingAfter;
  };

  doc.setProperties({ title: `${TERMS_TITLE} v${terms.version}` });
  write(TERMS_TITLE, 16, "bold", 2);
  write(
    `Version ${terms.version}, valid from ${formatTermsDate(terms.validFrom)}`,
    10,
    "normal",
    6
  );
  terms.sections.forEach((section, index) => {
    write(`${index + 1}. ${section.title}`, 11, "bold", 1);
    write(section.body, 10, "normal", 5);
  });

  const pageCount = doc.getNumberOfPages();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  for (let page = 1; page <= pageCount; page++) {
    doc.setPage(page);
    doc.text(
      `Page ${page} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - FOOTER_OFFSET,
      { align: "center" }
    );
  }

  await doc.save(termsPdfFileName(terms.version), { returnPromise: true });
}
